export interface RetryProps<T> {
    /**
     * [optional] How long we should continue to retry for before giving up. Defaults to 30000ms
     */
    timeoutMillis?: number;

    /**
     * [optional] The amount of time we should wait between calls. Defaults to 2000ms
     * */
    callIntervalMillis?: number;

    /**
     * [optional] The result of `callable` must pass this check, otherwise we will try calling again
     */
    successCondition?: (result: T) => boolean;
}

export interface RetryResponse<T> {
    /**
     * Signals the retry to stop before the next call occurs
     */
    terminate: () => void;

    /**
     * Starts the retry process
     */
    execute: () => Promise<T | undefined>;
}

/**
 * A helper function to perform automatic retries on a callable until we get the response we want
 * @param callable The function we want to retry calling
 * @param props [optional] Optional configuration for this retry
 */
export function Retry<T>(
    callable: () => Promise<T>,
    {
        timeoutMillis = 30000,
        callIntervalMillis = 2000,
        successCondition = () => true,
    }: RetryProps<T> = {},
): RetryResponse<T> {
    let shouldCancel = false;

    const wait = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms));

    const terminate = () => {
        shouldCancel = true;
    };

    const execute = async () => {
        const startMillis = performance.now();
        let curMillis = startMillis;
        while (!shouldCancel && curMillis - startMillis < timeoutMillis) {
            const result = await callable();
            if (successCondition(result)) {
                return result;
            }
            await wait(callIntervalMillis);
            curMillis = performance.now();
        }
    };

    return {
        terminate,
        execute,
    };
}
